import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { AlertCircle, CheckCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlatformSettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      setSaving(true);
      const updateData = { [key]: value };
      
      await axios.patch(
        `${API}/admin/platform-settings`,
        updateData,
        { withCredentials: true }
      );

      setSettings({ ...settings, [key]: value });
      toast.success(`${key === 'mls_features_enabled' ? 'MLS Features' : 'Setting'} ${value ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings. Super admin access required.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading platform settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
            <Settings className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Platform Settings</h2>
            <p className="text-sm text-gray-500">Control site-wide features and functionality</p>
          </div>
        </div>

        {/* MLS Features Toggle */}
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">🏆 MLS Partner Features</h3>
                  {settings?.mls_features_enabled ? (
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      ACTIVE
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      DISABLED
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-700 mb-4">
                  Controls visibility of MLS broker partnerships and upgrade options across the entire platform.
                </p>

                <div className="bg-white border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📍 Affected Areas:</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span><strong>Founding Members Banner</strong> - MLS upgrade options</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span><strong>Seller Resources Page</strong> - Flat-fee MLS broker section</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span><strong>Upgrade Page</strong> - MLS syndication tier</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span><strong>Service Provider Data</strong> - MLS broker partner cards</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span><strong>Site History</strong> - MLS-related timeline events</span>
                    </li>
                  </ul>
                </div>

                {settings?.mls_features_enabled && settings?.mls_enabled_at && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <strong>Enabled on:</strong> {new Date(settings.mls_enabled_at).toLocaleString()}
                    </p>
                  </div>
                )}

                {!settings?.mls_features_enabled && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-900 font-semibold mb-1">Currently Hidden</p>
                        <p className="text-sm text-amber-800">
                          All MLS-related content is hidden from users. Enable once broker partnerships are secured.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="ml-6">
                <Switch
                  checked={settings?.mls_features_enabled || false}
                  onCheckedChange={(checked) => updateSetting('mls_features_enabled', checked)}
                  disabled={saving}
                  className="data-[state=checked]:bg-green-600"
                />
              </div>
            </div>
          </div>

          {/* Maintenance Mode Toggle */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">🛠️ Maintenance Mode</h3>
                  {settings?.maintenance_mode ? (
                    <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                      ACTIVE
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                      OFF
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-700 mb-3">
                  Show a maintenance banner to all users (for scheduled updates or fixes).
                </p>

                {settings?.maintenance_mode && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                    <p className="text-sm text-red-800 font-semibold">
                      ⚠️ Users will see a maintenance notice banner
                    </p>
                  </div>
                )}
              </div>

              <div className="ml-6">
                <Switch
                  checked={settings?.maintenance_mode || false}
                  onCheckedChange={(checked) => updateSetting('maintenance_mode', checked)}
                  disabled={saving}
                  className="data-[state=checked]:bg-red-600"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Last Updated Info */}
        {settings?.updated_at && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              <strong>Last updated:</strong> {new Date(settings.updated_at).toLocaleString()}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PlatformSettingsManager;
